<template>
  <div class="form-container">
    <h3 class="text-center">enrol profile {{enrolProfile.enrolId}}</h3>

    <VeeForm v-slot="{ invalid }">
      <form autocomplete="off">

        <div class="form-group">
          <label for="description">description</label>
          <Field name="description" rules="min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="description" v-bind="field" v-model="description">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <SectionHeader title="actility" class="mt-5"/>

        <div class="form-group">
          <label for="baseUrl">base url</label>
          <Field name="baseUrl" rules="required|url" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="baseUrl" v-bind="field" v-model="actility.baseUrl">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="username">username</label>
          <Field name="username" rules="required|email" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="username" v-bind="field" v-model="actility.username">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="password">password</label>
          <Field name="password" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="password" v-bind="field" v-model="actility.password">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="profileName">profileName</label>
          <Field name="profileName" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="profileName" v-bind="field" v-model="actility.profileName">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="n2nUrl">n2n url</label>
          <Field name="n2nUrl" rules="required|url" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="n2nUrl" v-bind="field" v-model="actility.n2nUrl">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="devProfileClassA">device profile class A</label>
          <Field vid="devProfileClassA" name="devProfileClassA" :rules="{ required: !actility.devProfileClassC, min: 3 }" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="devProfileClassA" v-bind="field" v-model="actility.devProfileClassA">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="devProfileClassA">device profile class C</label>
          <Field vid="devProfileClassC" name="devProfileClassC" :rules="{ required: !actility.devProfileClassA, min: 3 }" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="devProfileClassC" v-bind="field" v-model="actility.devProfileClassC">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="networkSubscription">connectivity plan</label>
          <Field name="networkSubscription" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="networkSubscription" v-bind="field" v-model="actility.networkSubscription">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <button 
          class="btn btn-info new-device-btn"
          type="submit" 
          @click.prevent="update()" 
          :disabled="invalid || !canUpdate"
>
          <font-awesome-icon icon="plus"/> update enrol profile
        </button>

        <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
      </form>

    
    </VeeForm>

    <Error :label="error"/>
    <Success :label="success"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Error from '@/components/Error.vue';
import Success from '@/components/Success.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { EnrolProfile, EnrolProfileActility } from '@/types';
import { ApiClient, curApiClient } from '@/services/ApiClient';

interface Props {
  enrolProfile: EnrolProfile;
}

const props = withDefaults(defineProps<Props>(), {
  enrolProfile: () => ({}) as EnrolProfile
});

const emit = defineEmits<{
  cancel: [];
}>();

const router = useRouter();

// our model
const error = ref<string>('');
const success = ref<string>('');

const description = ref<string>('');
const actility = ref<EnrolProfileActility>({} as EnrolProfileActility);

const httpBusy = ref<boolean>(false);

// internal dependencies
const apiClient: ApiClient = curApiClient;

// Watch for enrolProfile changes
watch(() => props.enrolProfile, (newValue: EnrolProfile, _oldValue: EnrolProfile | undefined) => {
  // we update our model with the start up data
  description.value = newValue.description;
  
  if(newValue.actility)
    actility.value = Object.assign({}, newValue.actility);
}, { immediate: true });

onMounted(async () => {
  // Component mounted
});

const canUpdate = computed<boolean>(() => {
  return !httpBusy.value;
});

const update = async () => {
  httpBusy.value = true;
  error.value = '';

  const updateReq = {
    enrolId: props.enrolProfile.enrolId,
    description: description.value,
    actility: actility.value
  };
  
  const reply = await apiClient.updateEnrolProfile(updateReq);

  httpBusy.value = false;
  
  if(reply.httpCode === 403)
    return router.push({name:'login'});

  if(reply.errorMsg)
    return error.value = `unable to update enrol profile: ${reply.errorMsg}`;

  setSuccess(`Enrol profile ${props.enrolProfile.enrolId} updated successfully`);
};

const setSuccess = (str: string) => {
  success.value = str;
  setTimeout(() => emit('cancel'), 1000);
};

const cancel = () => {
  emit('cancel');
};
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

label {
  font-weight: bold;
  color: $bgColour;
}

input::placeholder {
  color: $linkColour;
}

.error {
  color: red;
  font-size: 0.75em;
  font-weight: bold;
  margin-top:10px
}

form button {
  margin: 3px;
}

.tip {
  font-size: 0.7rem;
  font-style: italic;
  color: #999;
}
</style>